import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FolderOpen,
  Loader2,
  RefreshCw,
  Upload,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fontManagerApi } from '../services/api';
import './FontManager.css';

function faceLabel(face) {
  if (face.subfamilyName) return face.subfamilyName;
  const weight = face.weight >= 700 ? 'Bold' : 'Regular';
  return face.italic ? `${weight} Italic` : weight;
}

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

function supportedFontFiles(fileList) {
  return Array.from(fileList || []).filter(file => /\.(ttf|otf|woff|woff2)$/i.test(file.name));
}

function FontManager() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [uploadState, setUploadState] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [mutating, setMutating] = useState(false);
  const [busyFace, setBusyFace] = useState(null);
  const dialogRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const loadCatalog = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setLoadError('');
    try {
      const response = await fontManagerApi.list();
      setCatalog(response.data);
    } catch (error) {
      setLoadError(error.response?.data?.error || 'Failed to load the font catalog');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    if (!uploadState) return undefined;
    const previous = document.activeElement;
    dialogRef.current?.focus();
    const handleKeyDown = event => {
      if (event.key === 'Escape' && !mutating) setUploadState(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previous?.focus?.();
    };
  }, [uploadState, mutating]);

  const families = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return catalog?.families || [];
    return (catalog?.families || []).filter(family => (
      family.familyName.toLocaleLowerCase().includes(query)
      || family.faces.some(face => (
        faceLabel(face).toLocaleLowerCase().includes(query)
        || face.activeVersion?.originalFilename?.toLocaleLowerCase().includes(query)
      ))
    ));
  }, [catalog, search]);

  const closeUpload = () => {
    if (mutating) return;
    setUploadState(null);
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const openUpload = (face = null) => {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    setUploadState(face ? { mode: 'replace', face } : { mode: 'upload' });
  };

  const selectFontFiles = fileList => {
    const files = supportedFontFiles(fileList);
    setSelectedFiles(files);
    if (files.length === 0) toast.error('The selection did not contain any TTF, OTF, WOFF, or WOFF2 files');
  };

  const submitUpload = async () => {
    if (!selectedFiles.length || mutating) return;
    setMutating(true);
    try {
      const response = await fontManagerApi.upload(
        selectedFiles,
        uploadState?.mode === 'replace' ? uploadState.face.faceKey : null
      );
      setCatalog(response.data);
      toast.success(uploadState?.mode === 'replace' ? 'Font face replaced and activated' : 'Fonts uploaded and activated');
      setUploadState(null);
      setSelectedFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Font activation failed');
    } finally {
      setMutating(false);
    }
  };

  const refreshRuntime = async () => {
    setMutating(true);
    try {
      const response = await fontManagerApi.refresh();
      setCatalog(response.data);
      toast.success('ONLYOFFICE font inventory refreshed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Runtime refresh failed');
    } finally {
      setMutating(false);
    }
  };

  const rollback = async face => {
    const target = face.rollbackVersion
      ? `${face.rollbackVersion.originalFilename}`
      : 'the system baseline';
    if (!window.confirm(`Roll back ${face.familyName} ${faceLabel(face)} to ${target}? Existing documents may reflow.`)) return;
    setBusyFace(face.faceKey);
    try {
      const response = await fontManagerApi.rollback(face.faceKey);
      setCatalog(response.data);
      toast.success('Previous font version restored');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Font rollback failed');
    } finally {
      setBusyFace(null);
    }
  };

  const deactivate = async face => {
    if (!window.confirm(`Deactivate the custom ${face.familyName} ${faceLabel(face)} face? Existing documents may render differently.`)) return;
    setBusyFace(face.faceKey);
    try {
      const response = await fontManagerApi.deactivate(face.faceKey);
      setCatalog(response.data);
      toast.success('Custom font face deactivated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Font deactivation failed');
    } finally {
      setBusyFace(null);
    }
  };

  const toggleFamily = familyName => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(familyName)) next.delete(familyName);
      else next.add(familyName);
      return next;
    });
  };

  return (
    <div className="font-manager-page">
      <div className="font-manager-breadcrumb"><Link to="/settings">Settings</Link><span>/</span>Font Manager</div>
      <header className="font-manager-header">
        <div>
          <h1>Font Manager</h1>
          <p>Manage the fonts available globally in Word and Canvas documents. Uploads activate after the live ONLYOFFICE refresh completes.</p>
        </div>
        <button type="button" className="btn btn-primary font-manager-upload" onClick={() => openUpload()}>
          <Upload size={16} /> Upload fonts
        </button>
      </header>

      {loading ? (
        <div className="font-manager-state" role="status"><Loader2 className="spin" size={20} /> Loading font inventory…</div>
      ) : loadError ? (
        <div className="font-manager-state font-manager-state--error" role="alert">
          <AlertTriangle size={20} />
          <span>{loadError}</span>
          <button type="button" className="btn" onClick={() => loadCatalog()}>Retry</button>
        </div>
      ) : (
        <>
          <section className={`font-runtime-status ${catalog.runtime.status !== 'synchronized' ? 'font-runtime-status--error' : ''}`}>
            {catalog.runtime.status === 'synchronized'
              ? <CheckCircle2 size={19} aria-hidden="true" />
              : <AlertTriangle size={19} aria-hidden="true" />}
            <div>
              <strong>{catalog.runtime.status === 'synchronized' ? 'ONLYOFFICE runtime is synchronized' : 'ONLYOFFICE runtime needs attention'}</strong>
              <span>
                {catalog.runtime.status === 'synchronized'
                  ? `Document Server ${catalog.runtime.documentServerVersion || 'unknown'}${catalog.runtime.refreshedAt ? ` · refreshed ${formatDate(catalog.runtime.refreshedAt)}` : ''}`
                  : catalog.runtime.error || 'Runtime inventory is unavailable'}
              </span>
            </div>
            <div className="font-runtime-totals">
              <strong>{catalog.totals.families} families</strong>
              <span>{catalog.totals.activeFiles} active faces</span>
            </div>
          </section>

          <section className="font-family-card">
            <div className="font-manager-tools">
              <label className="sr-only" htmlFor="font-manager-search">Search fonts</label>
              <input
                id="font-manager-search"
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search font families…"
              />
              <button type="button" className="btn" onClick={refreshRuntime} disabled={mutating}>
                <RefreshCw size={15} className={mutating ? 'spin' : ''} /> Refresh runtime
              </button>
            </div>

            {families.length === 0 ? (
              <div className="font-manager-empty">{search ? 'No matching font families.' : 'No fonts were reported by the runtime.'}</div>
            ) : families.map(family => {
              const isExpanded = expanded.has(family.familyName);
              const loadedFaces = family.faces.filter(face => face.runtimeLoaded).length;
              return (
                <div className={`font-family ${isExpanded ? 'is-open' : ''}`} key={family.familyName}>
                  <button type="button" className="font-family-summary" onClick={() => toggleFamily(family.familyName)} aria-expanded={isExpanded}>
                    <span className="font-family-sample" style={{ fontFamily: `"${family.familyName}", sans-serif` }}>Aa</span>
                    <span className="font-family-identity">
                      <strong>{family.familyName}</strong>
                      <small>{loadedFaces} active {loadedFaces === 1 ? 'face' : 'faces'}</small>
                    </span>
                    <span className="font-face-pills">
                      {family.faces.map(face => <span key={face.faceKey}>{faceLabel(face)}</span>)}
                    </span>
                    <span className={family.faces.every(face => face.runtimeLoaded) ? 'font-loaded' : 'font-loaded font-loaded--warning'}>
                      {family.faces.every(face => face.runtimeLoaded) ? '● Loaded' : '● Attention'}
                    </span>
                    <ChevronDown className="font-family-chevron" size={17} aria-hidden="true" />
                  </button>

                  {isExpanded && (
                    <div className="font-family-details">
                      {family.faces.map(face => {
                        const current = face.activeVersion || face.latestVersion;
                        const isBusy = busyFace === face.faceKey;
                        return (
                          <div className="font-face-row" key={face.faceKey}>
                            <div>
                              <strong>{faceLabel(face)}</strong>
                              <small>
                                {face.source === 'system' ? 'System baseline' : 'Uploaded'}
                                {current?.createdAt ? ` · ${formatDate(current.createdAt)}` : ''}
                              </small>
                            </div>
                            <code>
                              {current?.originalFilename || face.runtimeFilename || face.postscriptName || 'System font'}
                              {current?.browserArtifact ? ` + ${current.browserArtifact.originalFilename}` : ''}
                            </code>
                            <span className={face.runtimeLoaded ? 'font-loaded' : 'font-loaded font-loaded--warning'}>
                              {face.runtimeLoaded ? 'Runtime verified' : 'Not loaded'}
                            </span>
                            <div className="font-face-actions">
                              <button type="button" className="btn btn-small" onClick={() => openUpload(face)} disabled={isBusy || mutating}>Replace</button>
                              {face.activeVersion && (face.rollbackVersion || face.rollbackToSystem) && (
                                <button type="button" className="btn btn-small" onClick={() => rollback(face)} disabled={isBusy || mutating}>Rollback</button>
                              )}
                              {face.activeVersion && (
                                <button type="button" className="btn btn-small btn-danger-text" onClick={() => deactivate(face)} disabled={isBusy || mutating}>Deactivate</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <p className="font-manager-footnote">
              Replacing or deactivating a face can change wrapping and pagination in existing documents. Deactivation removes the face from ONLYOFFICE while retaining its uploaded versions; permanent deletion is intentionally unavailable because existing DOCX files may still reference it.
            </p>
          </section>
        </>
      )}

      {uploadState && (
        <div className="font-upload-backdrop" onMouseDown={event => event.target === event.currentTarget && closeUpload()}>
          <section ref={dialogRef} className="font-upload-dialog" role="dialog" aria-modal="true" aria-labelledby="font-upload-title" tabIndex="-1">
            <header>
              <div>
                <h2 id="font-upload-title">
                  {uploadState.mode === 'replace'
                    ? `Replace ${uploadState.face.familyName} ${faceLabel(uploadState.face)}`
                    : 'Upload fonts'}
                </h2>
                <p>TTF/OTF files feed Word; matching WOFF2/WOFF companions feed Canvas. Publish or close open Word work first because activation briefly refreshes ONLYOFFICE.</p>
              </div>
              <button type="button" className="font-upload-close" onClick={closeUpload} disabled={mutating} aria-label="Close font upload">
                <X size={18} />
              </button>
            </header>
            <div className="font-upload-body">
              {uploadState.mode === 'replace' && (
                <div className="font-reflow-warning"><AlertTriangle size={17} /> Replacing this face may change wrapping and pagination in existing documents.</div>
              )}
              <button type="button" className="font-dropzone" onClick={() => fileInputRef.current?.click()} disabled={mutating}>
                <Upload size={22} />
                <strong>{selectedFiles.length ? `${selectedFiles.length} font ${selectedFiles.length === 1 ? 'file' : 'files'} selected` : 'Choose font files'}</strong>
                <span>Choose matching .ttf/.otf and optional .woff2/.woff companions</span>
              </button>
              <button type="button" className="btn font-folder-picker" onClick={() => folderInputRef.current?.click()} disabled={mutating}>
                <FolderOpen size={16} /> Choose a font folder
              </button>
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
                multiple
                onChange={event => selectFontFiles(event.target.files)}
              />
              <input
                ref={folderInputRef}
                hidden
                type="file"
                multiple
                webkitdirectory=""
                directory=""
                onChange={event => selectFontFiles(event.target.files)}
              />
              {selectedFiles.length > 0 && (
                <ul className="font-upload-files">
                  {selectedFiles.map(file => <li key={`${file.webkitRelativePath || file.name}-${file.size}`}><span>{file.webkitRelativePath || file.name}</span><small>{Math.ceil(file.size / 1024)} KB</small></li>)}
                </ul>
              )}
              {mutating && (
                <div className="font-activation-progress" role="status" aria-live="polite">
                  <Loader2 className="spin" size={18} />
                  <div>
                    <strong>Activating fonts in ONLYOFFICE…</strong>
                    <span>This usually takes about a minute while the font registry is rebuilt. Keep this window open.</span>
                  </div>
                </div>
              )}
            </div>
            <footer>
              <button type="button" className="btn" onClick={closeUpload} disabled={mutating}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={submitUpload} disabled={!selectedFiles.length || mutating}>
                {mutating ? <><Loader2 className="spin" size={16} /> Activating…</> : 'Upload and activate'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

export default FontManager;
